import { HandlersCollection, RefBool, RefNumber, RefString, RefWatcher } from "@tblabs/truffle";
import { DeviceConfigForWatcher } from "../../../Models/DeviceConfig";
import { Collection } from "@tblabs/truffle";
import { Throttle } from "../../../Utils/Throttle/Throttle";
import { Preset } from "./Preset";
import { PresetsProvider } from "./PresetsProvider";


export class CameraPictureControl
{
    public ShowCustomConfigurator = new RefBool();
    public CurrentPresetName = new RefString();
    public PictureWidth = new RefNumber();
    public PictureHeight = new RefNumber();
    public PictureMaxWidth = 2592;
    public PictureMaxHeight = 1944;
    public PictureBrightness = new RefNumber();
    public PictureContrast = new RefNumber();
    public PictureRotation = new RefNumber(); // This comes from <select> so value becomes string!
    public OnChange = new HandlersCollection<(preset: Preset) => void>();
    public ThrottledOnChange = new HandlersCollection<(preset: Preset) => void>();

    private throttle = new Throttle({ Interval: 10, Steps: 100 });
    public ThrottleProgressPercent = this.throttle.ProgressPercent;

    constructor(private _presets: PresetsProvider)
    {
        this.CurrentPresetName.OnChange((name: string) =>
        {
            const preset = this._presets.Presets.Find(p => p.Name === name);
            if (preset)
            {
                this.OnChange.Hold();
                this.PictureWidth.value = preset.Width;
                this.PictureHeight.value = preset.Height;
                this.PictureBrightness.value = preset.Brightness;
                this.PictureContrast.value = preset.Contrast;
                this.PictureRotation.value = +preset.Rotation;
                this.OnChange.Release();
                this.OnChange.Call(this.BuildPreset());

                this.ShowCustomConfigurator.value = name.startsWith("custom");
            }
        });

        RefWatcher.Watch([
            this.PictureWidth,
            this.PictureHeight,
            this.PictureBrightness,
            this.PictureContrast,
            this.PictureRotation],
            () => this.OnChange.Call(this.BuildPreset()));

        this.OnChange.Subscribe((preset: Preset) =>
        {
            _presets.Edit(preset);

            this.throttle.CallLater(() =>
            {
                this.ThrottledOnChange.Call(preset);
            });
        });
    }

    private BuildPreset(): Preset
    {
        return new Preset(
            this.CurrentPresetName.value,
            this.CurrentPresetName.value,
            this.PictureWidth.value,
            this.PictureHeight.value,
            this.PictureBrightness.value,
            this.PictureContrast.value,
            +this.PictureRotation.value);
    }

    public Set(config: Partial<DeviceConfigForWatcher>): void
    {
        if (!config) return;
        
        this.OnChange.Hold();
        this.CurrentPresetName.OnChangeHandler.Hold();

        if (config.Picture)
        {
            this.CurrentPresetName.value = config.Picture.Name ?? "optimal"; // default value here ("optimal") is for backward compatibility for devices v.3.0.0 or lower
            this.PictureWidth.value = config.Picture.Width ?? 0;
            this.PictureHeight.value = config.Picture.Height ?? 0;
            this.PictureBrightness.value = config.Picture.Brightness ?? 0;
            this.PictureContrast.value = config.Picture.Contrast ?? 0;
            this.PictureRotation.value = config.Picture.Rotation ?? 0;
        }

        if (config.Hardware?.Camera?.MaxWidth && config.Hardware?.Camera?.MaxHeight)
        {
            this.PictureMaxWidth = config.Hardware.Camera.MaxWidth;
            this.PictureMaxHeight = config.Hardware.Camera.MaxHeight;

            const maxPreset = this.Presets.Items.find(p => p.Name === "max");
            if (maxPreset)
            {
                maxPreset.Width = this.PictureMaxWidth;
                maxPreset.Height = this.PictureMaxHeight;
                this._presets.Edit(maxPreset);
            }
        }

        this.OnChange.Release();
        this.CurrentPresetName.OnChangeHandler.Release();

        this.ShowCustomConfigurator.value = this.CurrentPresetName.value.startsWith("custom");
    }

    public get Presets(): Collection<Preset>
    {
        return this._presets.Presets;
    }
}
